import { Logger } from "@classdojo/monsterverse-utils";
import { createTheme, ThemeProvider } from "@mui/material";
import type * as pc from "playcanvas";
import { Component, type ComponentChildren, createRef } from "preact";

import { NotificationUI } from "./NotificationUI";

type RootProps = {
  waitForAppLoad?: boolean;
  children?: ComponentChildren;
};

export class Root extends Component<RootProps> {
  private ref = createRef();
  private loads = 0;
  private loadsNeeded = 0;
  protected app: pc.AppBase;
  private appReady = false;
  private appStarted = false;

  state = { render: false, loadingDone: false };

  constructor() {
    super();

    this.app = window.app;
    this.app.on("appReady", () => {
      this.appReady = true;
      this.checkForReadyToRender();
    });
    this.app.on("start", () => {
      this.appStarted = true;
      this.checkForReadyToRender();
    });
  }

  private checkForReadyToRender() {
    if (this.appReady && this.appStarted) {
      this.setState({ render: true });
    }
  }

  private loadingDone() {
    this.setState({ loadingDone: true });
    this.app.fire("uiLoaded");
  }

  private getAllChildren(children: HTMLCollection, callback: (child: Element) => void) {
    for (const child of children) {
      callback(child);
      this.getAllChildren(child.children, callback);
    }
  }

  override componentDidMount() {
    this.getAllChildren(this.ref.current.children, (child: Element) => {
      if (child instanceof HTMLImageElement) {
        this.loadsNeeded++;
        if (child.complete) {
          this.loads++;
        } else {
          const onLoad = () => {
            this.loads++;
            if (this.loads >= this.loadsNeeded) {
              this.loadingDone();
            }
          };
          child.addEventListener("load", onLoad);
          child.addEventListener("error", () => {
            Logger.logWarn(`Error loading img src for: ${child.outerHTML}`);
            onLoad();
          });
        }
      }
    });

    //Check if we are already done loading (don't have any images or already loaded)
    if (this.loads >= this.loadsNeeded) {
      this.loadingDone();
    }
  }

  render() {
    const theme = createTheme({
      typography: {
        fontFamily: ["OmoType", "Helvetica Neue", "Helvetica", "Arial", "sans-serif"].join(","),
      },
    });

    return (
      <ThemeProvider theme={theme}>
        <div hidden={(this.props.waitForAppLoad && !this.state.render) || !this.state.loadingDone} ref={this.ref}>
          {this.props.children}
        </div>
        <NotificationUI />
      </ThemeProvider>
    );
  }
}

Root.defaultProps = {
  waitForAppLoad: true,
};
